<script setup>
  import { useTemplateStore } from '@/stores/template'
  import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
  import VueSelect from 'vue-select'
  import PublinkerCloudIcon from '@/components/PublinkerCloudIcon.vue'
  import PublinkerUserDropdown from '@/components/PublinkerUserDropdown.vue'
  import PublinkerEnvironmentIndicator from '@/components/PublinkerEnvironmentIndicator.vue'
  import PublinkerFavoriteLinks from '@/components/PublinkerFavoriteLinks.vue'

  const store = useTemplateStore()
  const radioDarkMode = ref('light')
  const isReadyKeys = ['permissions']
  const isReady = computed(() => {
    return isReadyKeys.map(key => !store.isLoading.includes(key)).every(Boolean)
  })

  const tempSelectedSite = ref(store.site)

  watch(
    () => store.site,
    (site, oldSite) => {
      if (!oldSite || site != oldSite) {
        tempSelectedSite.value = site
      }
    }
  )

  // Emits
  const emit = defineEmits(['setSite'])

  const confirmSiteSelection = newValue => {
    // Emite o evento diretamente quando o valor do dropdown é alterado
    emit('setSite', newValue)

    // Atualiza o título da página
    document.title = `PubLinker | ${newValue}`

    // Fecha o dropdown se necessário
    const dropdownElement = document.getElementById('page-header-user-dropdown')
    const dropdownFloatElement = document.getElementById(
      'page-header-user-dropdown-float'
    )

    if (dropdownElement) {
      dropdownElement.classList.remove('show')
    }
    if (dropdownFloatElement) {
      dropdownFloatElement.classList.remove('show')
    }
  }

  const siteSelector = ref(null)

  function toggleDarkMode() {
    if (store.settings.darkMode) {
      store.darkMode({ mode: 'off' })
      radioDarkMode.value = 'light'
    } else {
      store.darkMode({ mode: 'on' })
      radioDarkMode.value = 'dark'
    }
  }

  // Define o valor inicial do botão baseado no dark mode atual
  function setDarkModeRadioDefault() {
    if (store.settings.darkMode) {
      radioDarkMode.value = 'dark'
    } else {
      radioDarkMode.value = 'light'
    }
  }

  // Atualiza o valor do botão quando o dark mode muda no store
  watch(
    () => store.settings.darkMode,
    () => {
      setDarkModeRadioDefault()
    }
  )

  // Inicializa o estado correto ao carregar
  setDarkModeRadioDefault()

  // Inicializa o header como fixo
  onMounted(() => {
    document.documentElement.classList.add('header-fixed')
    store.settings.headerFixed = true

    // Atualiza o título com o site inicial
    if (store.site) {
      document.title = `PubLinker | ${store.site}`
    }
  })

  // Remove a classe quando o componente é desmontado
  onUnmounted(() => {
    document.documentElement.classList.remove('header-fixed')
  })

  const props = defineProps({
    MODE: String,
    envVars: Object,
    VITE_PUBLINKER_API_BASE: String,
    VITE_LEADCLUMP_API_BASE: String
  })
</script>

<template>
  <!-- Header -->
  <header
    id="page-header"
    class="header-fixed"
    :class="[
      {
        'with-sidebar':
          store.settings.sidebarVisibleDesktop && !store.settings.sidebarMini
      },
      {
        'with-sidebar-mini':
          store.settings.sidebarVisibleDesktop && store.settings.sidebarMini
      }
    ]"
    style="z-index: 1000"
  >
    <slot>
      <!-- Header Content -->
      <div class="content-header">
        <slot name="content">
          <!-- Left Section -->
          <div class="d-flex align-items-center">
            <slot name="content-left">
              <button
                type="button"
                class="header-btn d-lg-none"
                @click="store.sidebar({ mode: 'toggle' })"
              >
                <i class="fa fa-fw fa-bars"></i>
              </button>
              <button
                type="button"
                class="header-btn d-none d-lg-inline-block"
                @click="store.sidebarMini({ mode: 'toggle' })"
              >
                <i
                  class="fa"
                  :class="
                    store.settings.sidebarMini
                      ? 'menu-expand-right'
                      : 'menu-expand-left'
                  "
                ></i>
              </button>
              <button
                type="button"
                class="header-btn d-none d-lg-inline-block"
                @click="toggleDarkMode"
                id="sidebar-dark-mode-dropdown"
              >
                <i
                  class="fa"
                  :class="radioDarkMode === 'light' ? 'fa-sun' : 'fa-moon'"
                ></i>
              </button>
            </slot>
          </div>
          <!-- END Left Section -->
          <div v-if="store.isDemo" class="alert alert-warning demo-alert">
            Demo Mode is active
          </div>
          <!-- Right Section -->
          <div class="d-flex align-items-center">
            <slot name="content-right">
              <div class="d-flex justify-content-end align-items-center">
                <PublinkerEnvironmentIndicator
                  v-if="props.MODE == 'homolog' || props.MODE == 'development'"
                  :mode="props.MODE"
                  :envVars="props.envVars"
                  :vitePublinkerApi="props.VITE_PUBLINKER_API_BASE"
                  :viteLeadclumpApi="props.VITE_LEADCLUMP_API_BASE"
                />
                <PublinkerFavoriteLinks />
                <PublinkerCloudIcon />
                <VueSelect
                  ref="siteSelector"
                  v-model="tempSelectedSite"
                  class="siteSelector"
                  :options="store.siteList"
                  :loading="!isReady"
                  :clearable="false"
                  :multiple="false"
                  @update:model-value="confirmSiteSelection"
                  :class="{ blur: store.isDemo }"
                />
              </div>

              <PublinkerUserDropdown />

              <!-- Notifications Dropdown -- >
              <div class="dropdown d-inline-block ms-2">
                <button
                  type="button"
                  class="btn btn-sm btn-alt-secondary space-x-1"
                  id="page-header-notifications-dropdown"
                  data-bs-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <i class="fa fa-fw fa-bell"></i>
                  <span v-if="notifications.length > 0" class="text-primary"
                    >•</span
                  >
                </button>
                <div
                  class="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 border-0 fs-sm"
                  aria-labelledby="page-header-notifications-dropdown"
                >
                  <div
                    class="p-2 bg-body-light border-bottom text-center rounded-top"
                  >
                    <h5 class="dropdown-header text-uppercase">
                      Notifications
                    </h5>
                  </div>
                  <ul class="nav-items mb-0">
                    <li
                      v-for="(notification, index) in notifications"
                      :key="`notification-${index}`"
                    >
                      <a
                        class="text-dark d-flex py-2"
                        :href="`${notification.href}`"
                      >
                        <div class="flex-shrink-0 me-2 ms-3">
                          <i :class="`${notification.icon}`"></i>
                        </div>
                        <div class="flex-grow-1 pe-2">
                          <div class="fw-semibold">
                            {{ notification.title }}
                          </div>
                          <span class="fw-medium text-muted">
                            {{ notification.time }}
                          </span>
                        </div>
                      </a>
                    </li>
                    <li v-if="!notifications.length" class="p-2">
                      <div
                        class="alert alert-light d-flex align-items-center space-x-2 mb-0"
                        role="alert"
                      >
                        <i class="fa fa-exclamation-triangle opacity-50"></i>
                        <p class="mb-0">No new ones!</p>
                      </div>
                    </li>
                  </ul>
                  <div
                    v-if="notifications.length > 0"
                    class="p-2 border-top text-center"
                  >
                    <a
                      class="d-inline-block fw-medium"
                      href="javascript:void(0)"
                    >
                      <i class="fa fa-fw fa-arrow-down me-1 opacity-50"></i>
                      Load More..
                    </a>
                  </div>
                </div>
              </div>
              < !-- END Notifications Dropdown -->
              <!-- Toggle Side Overlay -- >
              <button
                type="button"
                class="btn btn-sm btn-alt-secondary ms-2"
                @click="store.sideOverlay({ mode: 'toggle' })"
              >
                <i class="fa fa-fw fa-list-ul fa-flip-horizontal"></i>
              </button>
              < !-- END Toggle Side Overlay -->
            </slot>
          </div>
          <!-- END Right Section -->
        </slot>
      </div>
      <!-- END Header Content -->

      <!-- Header Loader -->
      <div
        id="page-header-loader"
        class="overlay-header bg-body-extra-light"
        :class="{ show: store.settings.headerLoader }"
      >
        <div class="content-header">
          <div class="w-100 text-center">
            <i class="fa fa-fw fa-circle-notch fa-spin"></i>
          </div>
        </div>
      </div>
      <!-- END Header Loader -->
    </slot>
  </header>
  <!-- END Header -->
</template>

<style lang="scss">
  @import 'bootstrap/scss/functions';
  @import 'bootstrap/scss/variables';

  .siteSelector {
    border-radius: 6px;
    .vs__selected {
      max-width: 211px;
    }
  }

  .siteSelector .vs__dropdown-toggle {
    line-height: 1;
    height: 38px;
    overflow: hidden;
    width: 250px;
    cursor: pointer !important;
  }

  .demo-alert {
    position: fixed;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80%;
    max-width: 300px;
    z-index: 1000;
    text-align: center;
  }

  .blur {
    filter: blur(5px);
  }

  .btn-profile {
    display: flex;
    justify-content: space-evenly;
    margin-top: 1rem;
  }

  .vs__selected {
    word-wrap: break-word;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  #page-header {
    position: relative;
    width: 100%;
    background-color: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.075);

    &.header-fixed {
      position: sticky;
      top: 0;
      box-shadow: 0 0.25rem 0.375rem rgba(0, 0, 0, 0.1);

      @media (min-width: 992px) {
        &.with-sidebar {
          padding-left: 250px;
        }

        &.with-sidebar-mini {
          padding-left: 66px;
        }
      }
    }

    .content-header {
      position: relative;
      width: 100%;
      padding: 0 1rem;
      height: 4rem;
      display: flex;
      align-items: center;

      @media (min-width: 992px) {
        padding: 0 2rem;
      }
    }
  }

  .fa-rotate-45 {
    transform: rotate(45deg);
  }

  // Definição da animação de pulso
  @keyframes pin-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(122, 38, 193, 0.4);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(122, 38, 193, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(122, 38, 193, 0);
    }
  }

  .header-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.25rem;
    height: 2.25rem;
    margin-right: 0.5rem;
    padding: 0;
    border: none;
    background-color: transparent;
    border-radius: 0.25rem;
    position: relative;
    transition: all 0.2s ease;

    i {
      font-size: 1rem;
      line-height: 1;
      width: 30px;
      height: 30px;
      padding: 6px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-sizing: border-box;
    }

    // Remove sombra de todos
    i::after {
      display: none !important;
    }

    // Simetria para todos os ícones
    & > i {
      margin: 0;
    }
  }

  // Sol com contorno para contraste
  .header-btn i.fa-sun {
    color: #e6a700; // Amarelo dourado mais escuro
    background: none;
    stroke: #bfae7c;
    stroke-width: 1.5px;
    filter: none;
  }

  // Lua azul
  .header-btn i.fa-moon {
    color: #5c8ae6;
    background: none;
    filter: none;
  }

  // Pin roxo
  .header-btn i.fa-thumbtack {
    color: #7a26c1;
    background: none;
    filter: none;
  }

  // Estado desativado do pin
  .header-btn i.fa-thumbtack.fa-rotate-45 {
    opacity: 0.7;
  }

  .dark-mode {
    .header-btn i.fa-sun {
      color: #ffd700;
      stroke: #bfae7c;
      stroke-width: 1.5px;
    }
    .header-btn i.fa-moon {
      color: #88a9f3;
    }
    .header-btn i.fa-thumbtack {
      color: #7ab992 !important;
    }
  }

  :root[class~='dark'] {
    #page-header {
      background-color: #1a1c23;
      border-bottom: 1px solid rgba(255, 255, 255, 0.05);

      &.header-fixed {
        box-shadow: 0 0.25rem 0.375rem rgba(0, 0, 0, 0.2);
      }

      .header-btn {
        i {
          &.fa-sun,
          &.fa-moon,
          &.fa-thumbtack {
            color: #9da5b5;
          }

          &.fa-thumbtack:not(.fa-rotate-45) {
            color: #85d939 !important; // Forçando a cor no modo escuro
          }
        }

        &:hover {
          background-color: rgba(255, 255, 255, 0.05);
        }

        &:active {
          background-color: rgba(255, 255, 255, 0.075);
        }
      }
    }
  }
</style>
